/**
 * Visor CAD (WebGL) para /planos, alternativo al render SVG — DETRÁS DEL FLAG
 * `VITE_PLANO_VIEWER_MLIGHTCAD` (ver `lib/planoGate.ts`). Usa `@mlightcad/cad-simple-viewer`
 * (MIT), que parsea y dibuja el DXF ENTERO en el navegador: el plano NO sale de la infra.
 *
 * Por qué existe: el SVG-DOM casero no escala a planos densos (miles de nodos). Este motor es
 * WebGL con índice espacial (pick sub-milisegundo) y render fiel (color por capa, cotas, hachura,
 * texto). Validado en el spike `scratchpad/spike-mlightcad/` (ver VEREDICTO.md).
 *
 * CONTRATO: emite exactamente la MISMA `PlanoSeleccion` que hoy emite el SVG
 *   - clic en geometría suelta → { tipo:'capa', clave: <capa>, ids:[handles de esa capa] }
 *   - clic en un bloque (INSERT) → { tipo:'bloque', clave:<blockName>, ids:[handles de ese bloque] }
 * `ids` = HANDLES del DXF, alineados con `inventario.ids` y con el `data-id` del SVG (linchpin de
 * identidad: `plano2svg.py` ya taguea con el handle). Así el flujo "asignar concepto → HP" y el
 * resaltado bidireccional plano↔HP funcionan idénticos con cualquiera de los dos visores.
 *
 * NAVEGACIÓN = COMO AUTOCAD (controles NATIVOS del visor, no los remapeamos):
 *   - rueda = zoom · botón medio (rueda) + arrastrar = pan · clic izq sin arrastre = seleccionar
 *     un objeto · arrastrar con izq = caja de selección (window/crossing) que maneja el visor.
 *   - la vista es 2D ortográfica: no rota.
 *
 * PEREZOSO: el motor (~16 MB) NO entra al bundle principal. Este componente lo monta PlanosPage con
 * `React.lazy`, y además el módulo del motor se carga con `import()` dinámico dentro del efecto de
 * init — solo cuando se abre un DXF de verdad.
 *
 * NO reemplaza al SVG ni al visor IFC: es un tercer camino, elegible por flag; el IFC sigue con
 * `IfcViewer3D`.
 */
import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import type { PlanoRenglon, PlanoSeleccion, PlanoVista } from '@supernova/shared';
import { fetchPlanoDxf } from '../api/planos.js';
import { handlesVisibles, hayOjosApagados } from '../lib/visibilidadPlano.js';
import { armarCensoDeCapas, type EntidadDelCenso } from '../lib/censoDeCapas.js';
import { reducirLienzo, type Croquis } from '../lib/croquis.js';
import { entidadesDelDibujo, type BaseDelVisor, type EntidadesLeidas } from '../lib/entidadesDelDibujo.js';

// Tipos del motor: SOLO para tipar (se borran en compilación, no arrastran el paquete al bundle).
import type { AcApDocManager } from '@mlightcad/cad-simple-viewer';
import type { AcCmColor, AcDbEntity } from '@mlightcad/data-model';

/**
 * Lo que el visor sabe de las entidades del dibujo, para quien necesita repartir algo entre ellas (p. ej. el «Avance de obra»):
 * los handles de una capa (o de la capa dentro de una vista) y de un bloque por su nombre, y el centro de cada entidad (unidades del dibujo).
 */
export interface CensoDelPlano {
  handlesDeCapa: (capa: string, vista?: string | null) => string[];
  handlesDeBloque: (nombre: string) => string[];
  /** Nombres de todas las capas y bloques del dibujo (el enlace del cliente los empareja con las huellas de la foto). */
  capas: () => string[];
  bloques: () => string[];
  centro: (handle: string) => { x: number; y: number } | undefined;
  /** Qué tan grande es la entidad en el dibujo (la diagonal de su caja, en unidades del dibujo): sirve de peso. Sin dato para textos, cotas y achurados. */
  tamano: (handle: string) => number | undefined;
  /** «Revisar el dibujo»: lee las entidades del documento YA abierto (una sola lectura por montaje, abortable). */
  entidades?: (o?: { signal?: AbortSignal; maxTrazosPorBloque?: number }) => Promise<EntidadesLeidas>;
}

interface Props {
  projectId: string;
  fileId: string;
  /**
   * Otra forma de traer el DXF (el enlace público del cliente: sin sesión, con su token). Ausente = el
   * DXF autenticado de siempre.
   */
  cargarDxf?: () => Promise<ArrayBuffer>;
  /** Renglones ya asignados (para resaltar en el plano lo que ya tiene concepto). */
  renglones: PlanoRenglon[];
  /** Selección actual de bloques, por HANDLE (la maneja PlanosPage; acá se resalta). */
  idsSel?: Set<string>;
  /** Capas seleccionadas actualmente, por nombre (idem). */
  capasSel?: Set<string>;
  /** Un clic sobre el plano produce una PlanoSeleccion; PlanosPage la traduce a su estado. */
  onSeleccion: (sel: PlanoSeleccion) => void;
  /**
   * Vistas del archivo (plantas, fachadas) con su caja en unidades del dibujo. Con ellas, "A-MUROS ·
   * Planta baja" resalta solo lo de esa planta, y un clic sobre una capa dice en qué planta cayó.
   */
  vistas?: PlanoVista[];
  /** La capa elegida SOLO dentro de una vista (null = la capa completa). */
  vistaSel?: { capa: string; vista: string } | null;
  /** true = el plano se muestra como miniatura (modo «eligiendo» de PlanosPage): sin sus controles. */
  sinControles?: boolean;
  /**
   * Para la miniatura: el visor entrega una función que dice DÓNDE está, en píxeles de su contenedor y con
   * la cámara de ahora, lo seleccionado (o todo el dibujo si no hay selección). No mueve la cámara.
   * Recibe null mientras el plano no está listo.
   */
  onCajaEnPantalla?: (leer: ((todo?: boolean) => CajaEnPantalla | null) | null) => void;
  /**
   * «¿Qué me falta?»: lo que SE CONSTRUYE (capas y bloques, por nombre). Con esto el plano se pinta:
   * rojo lo que no tiene concepto principal, gris lo que ya está en la hoja y tenue lo demás. Es solo
   * VISTA: al quitarlo cada entidad vuelve a su color. null/ausente = colores del plano.
   */
  queMeFalta?: { capas: string[]; bloques: string[] } | null;
  /**
   * Enfoque de la cámara: cada vez que cambia `n` (y el plano ya está listo) se encuadra lo que abarcan esas capas y
   * bloques (por nombre). Solo mueve la cámara; no pinta nada.
   */
  enfocar?: { n: number; capas: string[]; bloques: string[]; handles?: string[] } | null;
  /**
   * «Cambió el plano»: pinta lo que cambió (capas y bloques, por nombre) en el color de su rol y deja el resto tenue.
   * Usa el modo de comparación del motor (cambia cómo se DIBUJA, sin tocar el color de cada entidad: repintar entidad por
   * entidad congelaba el navegador con un dibujo de miles de entidades). Excluyente con `queMeFalta`.
   */
  resaltarCambio?: { capas: string[]; bloques: string[]; rol: 'added' | 'deleted' | 'modified' } | null;
  /**
   * «Cambió el plano», entidad por entidad: el rol de cada entidad de ESTE archivo (por handle). Manda sobre
   * `resaltarCambio`; si trae algo, se pinta eso (verde nuevo, rojo quitado, ámbar cambió) y el resto queda tenue.
   */
  comparacionPorEntidad?: ReadonlyMap<string, 'added' | 'deleted' | 'modified'> | null;
  /** Con `comparacionPorEntidad`: solo las entidades de estas capas y bloques (un concepto); el resto queda tenue. */
  filtroConcepto?: { capas: string[]; bloques: string[] } | null;
  /** Con `comparacionPorEntidad`: dónde está cada entidad (unidades del dibujo) para las que el motor no da centro (bloques sin extensión finita): solo para enfocar. */
  centrosExtra?: ReadonlyMap<string, { x: number; y: number }> | null;
  /**
   * Colores (0xRRGGBB) de los roles del modo de comparación, para quien los usa con otro sentido que «cambió el plano» (el «Avance de
   * obra»: `added` = lo construido, `modified` = lo que va en proceso). Ausente = verde, rojo y ámbar de siempre.
   */
  coloresComparacion?: { added?: number; deleted?: number; modified?: number; base?: number } | null;
  /** Al terminar de cargar el dibujo (y null al descargarlo): sus capas y bloques con sus handles y centros. */
  onCenso?: (censo: CensoDelPlano | null) => void;
  /**
   * Visibilidad por capa y por tramas (solo VISTA; lo oculto sigue contando en la hoja). Las capas apagadas (el ojo de la
   * lista «Capas») ocultan TODOS los handles de esa capa: su geometría suelta Y las inserciones de bloque puestas en ella
   * (como congelar una capa en AutoCAD). Conviven con «Aislar/Ocultar selección» en un solo conjunto de ocultos.
   */
  capasOcultas?: ReadonlySet<string>;
  /** Apaga todo HATCH (trama) sin importar su capa. */
  tramasOcultas?: boolean;
  /** Alterna «Ocultar tramas» / «Mostrar tramas». */
  onAlternarTramas?: () => void;
  /** «Mostrar todo» / Esc: el padre vuelve a prender las capas y las tramas. */
  onRestablecerOjos?: () => void;
  /**
   * Números generadores: el visor entrega una función que toma el CROQUIS (imagen PNG en data URL) de unos
   * elementos resaltados sobre el resto del plano atenuado, encuadrados. No cambia lo que ve la persona:
   * al terminar, el plano vuelve a como estaba. null mientras el plano no está listo.
   */
  onCapturador?: (
    capturar: ((elementos: ElementoParaCroquis[], marcas?: { k: string; elementos: ElementoParaCroquis[] }[]) => Promise<Croquis | null>) | null,
  ) => void;
}

/** Un elemento del plano para el croquis: una capa (o la capa en una vista) o un bloque (con sus handles). */
export interface ElementoParaCroquis {
  tipo: 'capa' | 'bloque';
  clave: string;
  vista?: string;
  ids?: string[];
}

// Colores del croquis (fondo blanco para imprimir): lo del renglón en el acento, el resto en gris claro.
const CROQUIS_FONDO = 0xffffff;
const CROQUIS_RESALTE = 0x0c6fd6;
const CROQUIS_RESTO = 0xc3c8ce;
const CROQUIS_ANCHO_MAX = 1100;

/** Rectángulo en píxeles del contenedor del visor. */
export interface CajaEnPantalla {
  x: number;
  y: number;
  w: number;
  h: number;
}

// --- Constantes de interacción de UI (no son datos de dominio) ---
// Radio de "acierto" del clic, en px: una línea es infinitamente delgada, así que el pick necesita
// tolerancia. El default nativo del visor (`selectionBoxSize`) es 4 px; le damos un poco más porque
// el trazo fino de un bloque (giro de puerta, símbolos) es más difícil de acertar que un muro largo.
const HIT_RADIUS_PX = 8;
// Umbral px para distinguir un CLIC (seleccionar) de un ARRASTRE: si el puntero se movió más que
// esto entre down y up, fue arrastre (pan con botón medio, o caja de selección con izq) y NO
// disparamos selección simple — dejamos que el visor maneje el gesto. Igual criterio que el SVG.
const CLICK_VS_DRAG_PX = 5;
// «¿Qué me falta?»: lo que se construye y no está en la hoja (mismo rojo que el visor 3D).
const COLOR_FALTA = 0xe5484d;
const COLOR_CAMBIO_NUEVO = 0x2ea043; // verde: lo nuevo (el mismo tono que en el modelo IFC)
const COLOR_CAMBIO_MEDIDA = 0xd9a400; // ámbar: lo que cambió de medida (idem IFC)

/** Modo de visibilidad del visor (equivalente a ISOLATEOBJECTS / HIDEOBJECTS de AutoCAD). Es solo
 *  VISTA: nunca toca la base del dibujo ni la selección; se puede volver a "todo" en cualquier momento. */
type VisMode = 'todo' | 'aislar' | 'ocultar';

export function PlanoViewerMlightcad({
  projectId,
  fileId,
  cargarDxf,
  renglones,
  idsSel,
  capasSel,
  onSeleccion,
  vistas,
  vistaSel,
  sinControles,
  onCajaEnPantalla,
  queMeFalta,
  enfocar,
  resaltarCambio,
  comparacionPorEntidad,
  filtroConcepto,
  centrosExtra,
  coloresComparacion,
  onCenso,
  onCapturador,
  capasOcultas,
  tramasOcultas = false,
  onAlternarTramas,
  onRestablecerOjos,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const dmRef = useRef<AcApDocManager | null>(null);
  const onCensoRef = useRef<Props['onCenso']>(onCenso);
  onCensoRef.current = onCenso;
  const [visorMuestraAvance, setVisorMuestraAvance] = useState(false);
  const [estado, setEstado] = useState<{ cargando: boolean; error: string | null }>({
    cargando: true,
    error: null,
  });
  // Aislar / ocultar la selección (color-independiente: no depende de qué color tenga el objeto).
  const [visMode, setVisMode] = useState<VisMode>('todo');

  // Censo del dibujo (se arma una vez al abrir): capa → handles, blockName → handles, handle → capa.
  const byLayerRef = useRef<Map<string, string[]>>(new Map());
  const byBlockRef = useRef<Map<string, string[]>>(new Map());
  const handleLayerRef = useRef<Map<string, string>>(new Map());
  const handleBlockRef = useRef<Map<string, string>>(new Map());
  // Centro de cada entidad (unidades del dibujo), para saber en qué vista cae.
  const handleCentroRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const handleTamanoRef = useRef<Map<string, number>>(new Map());
  // Tramas (HATCH de nivel superior) por el TIPO de la entidad; y lo que está oculto hoy (los clics lo ignoran).
  const handleTramaRef = useRef<Set<string>>(new Set());
  const ocultosRef = useRef<Set<string>>(new Set());
  /** Capas que apagó el ojo (nativo del motor): solo esas se vuelven a prender. */
  const capasApagadasRef = useRef<Set<string>>(new Set());
  // Ids actualmente resaltados (para poder quitar el resaltado anterior; no hay "unhighlightAll").
  const resaltadosRef = useRef<string[]>([]);
  // «¿Qué me falta?»: la entidad de cada handle (para cambiarle el color), el módulo del modelo de datos
  // (AcCmColor) y el color ORIGINAL de todo lo que se pintó, para devolverlo tal cual.
  const handleEntRef = useRef<Map<string, AcDbEntity>>(new Map());
  const dataModelRef = useRef<typeof import('@mlightcad/data-model') | null>(null);
  const coloresOriginalesRef = useRef<Map<AcDbEntity, AcCmColor>>(new Map());

  const haySeleccion = (idsSel?.size ?? 0) > 0 || (capasSel?.size ?? 0) > 0;

  // --- Init + apertura del DXF (perezoso: el motor se importa acá, no en el bundle principal) ---
  useEffect(() => {
    let cancelado = false;
    setEstado({ cargando: true, error: null });
    setVisorMuestraAvance(false);

    (async () => {
      try {
        const [cad, dm] = await Promise.all([
          import('@mlightcad/cad-simple-viewer'),
          import('@mlightcad/data-model'),
        ]);
        if (cancelado || !containerRef.current) return;
        dataModelRef.current = dm;
        coloresOriginalesRef.current = new Map();
        // Desde aquí el visor muestra SU propio avance ("Leyendo el plano…"): se apaga el nuestro para
        // no encimar dos avisos de carga.
        setVisorMuestraAvance(true);

        // Mensajes de carga en español (el visor los trae en inglés/chino). Se reescriben con su propia
        // API de textos sobre el idioma activo ('en'), así no hay que cambiar de locale.
        const i18n = (cad as { AcApI18n?: { mergeLocaleMessage?: (l: string, m: unknown) => void } }).AcApI18n;
        i18n?.mergeLocaleMessage?.('en', {
          main: {
            message: { fetchingDrawingFile: 'Descargando el plano…' },
            progress: {
              start: 'Leyendo el plano…',
              parse: 'Leyendo el plano…',
              font: 'Descargando las letras del plano…',
              ltype: 'Leyendo tipos de línea…',
              style: 'Leyendo estilos de texto…',
              dimstyle: 'Leyendo estilos de cota…',
              layer: 'Leyendo capas…',
              vport: 'Leyendo vistas…',
              blockrecord: 'Leyendo bloques…',
              header: 'Leyendo el encabezado…',
              block: 'Leyendo bloques…',
              entity: 'Leyendo el dibujo…',
              object: 'Leyendo el dibujo…',
              rendering: 'Dibujando el plano…',
              end: 'Listo',
            },
          },
        });

        // (Punto 1) Ocultar la UI estilo AutoCAD del visor que el cliente no debe ver: la barra de
        // comandos ("Type a command…"), el lector de coordenadas, la tarjeta de info y las stats.
        // Es la API oficial de settings, como override de SESIÓN (`persist:false`) para NO ensuciar
        // el localStorage compartido con el resto de la app. Se hace ANTES de createInstance para
        // que el InputManager nazca con la barra ya oculta (sin parpadeo). Las notificaciones/toasts
        // del visor se apagan aparte, con `notificationCenter:false` en createInstance.
        try {
          cad.AcApSettingManager.instance.apply(
            {
              isShowCommandLine: false,
              isShowCoordinate: false,
              isShowEntityInfo: false,
              isShowStats: false,
              // En modo lectura no hay edición: fuera la barra flotante de accesos (DESHACER /
              // REHACER / ELIMINAR = `isShowShortCutToolbar`), la toolbar y el ribbon del visor.
              isShowShortCutToolbar: false,
              isShowToolbar: false,
              isShowRibbon: false,
            },
            { persist: false },
          );
        } catch {
          /* best effort: si la forma de settings cambia, el visor sigue funcionando */
        }

        // El DocManager es un singleton. Si quedó uno de un montaje anterior, se destruye para
        // rebindear el canvas al contenedor nuevo de este montaje.
        const previo = cad.AcApDocManager.tryGetInstance();
        if (previo) await previo.destroy();
        if (cancelado || !containerRef.current) return;

        const instancia = cad.AcApDocManager.createInstance({
          container: containerRef.current,
          autoResize: true,
          webworkerFileUrls: { mtextRender: '/mtext-renderer-worker.js' },
          // (Punto 1) Sin centro de notificaciones: nada de bell/toasts del visor (ahí salía el
          // aviso de fuente faltante). El host maneja sus propios avisos por fuera.
          notificationCenter: false,
          // TODO(deploy aislado): auto-hospedar las fuentes que el motor baja de cdn.jsdelivr
          // (fonts.json + fuentes). Cuando estén en `apps/web/public/mlightcad-fonts/`, crear un
          // `DefaultFontLoader` con `baseUrl` local y pasarlo por `FontManager.setFontLoader`
          // (VEREDICTO §5). Por ahora usa el CDN por defecto — no bloquea el paso 1.
        });
        if (!instancia) {
          throw new Error('No se pudo iniciar el visor CAD.');
        }
        dmRef.current = instancia;
        capasApagadasRef.current.clear(); // un motor nuevo trae las capas como las dejó el dibujo

        // Descargar el DXF canónico (autenticado; el plano no sale de la infra) y abrirlo read-only.
        const buf = cargarDxf ? await cargarDxf() : await fetchPlanoDxf(projectId, fileId);
        if (cancelado) return;
        const ok = await instancia.openDocument(`plano-${fileId}.dxf`, buf, {
          mode: cad.AcEdOpenMode.Read,
        });
        if (cancelado) return;
        if (!ok) throw new Error('No se pudo leer el plano.');

        const view = instancia.curView;
        try {
          view.zoomToFitDrawing();
        } catch {
          /* mejor esfuerzo: si no encuadra, igual se ve */
        }

        armarCenso(instancia, dm.AcDbBlockReference);
        let lecturaEntidades: Promise<EntidadesLeidas> | null = null;
        onCensoRef.current?.({
          entidades: (o) => {
            if (!lecturaEntidades) {
              lecturaEntidades = entidadesDelDibujo(
                instancia.curDocument.database as unknown as BaseDelVisor,
                dm as unknown as Record<string, unknown>,
                o,
              ).catch((err) => {
                lecturaEntidades = null; // una lectura cancelada o fallida se puede volver a pedir
                throw err;
              });
            }
            return lecturaEntidades;
          },
          handlesDeCapa: (capa, vista) => handlesDeCapa(capa, vista),
          handlesDeBloque: (nombre) => handlesDeBloque(nombre),
          capas: () => [...new Set(handleLayerRef.current.values())],
          bloques: () => [...byBlockRef.current.keys()],
          centro: (h) => handleCentroRef.current.get(h),
          tamano: (h) => handleTamanoRef.current.get(h),
        });
        cablearClics(instancia);
        setEstado({ cargando: false, error: null });
        aplicarColorFalta();
        aplicarResaltado();
      } catch (err) {
        if (cancelado) return;
        setEstado({
          cargando: false,
          error: err instanceof Error ? err.message : 'No se pudo abrir el plano en el visor CAD.',
        });
      }
    })();

    return () => {
      cancelado = true;
      const inst = dmRef.current;
      dmRef.current = null;
      onCensoRef.current?.(null);
      // Fire-and-forget: liberar el motor al desmontar / cambiar de plano.
      if (inst) void inst.destroy().catch(() => { /* best effort */ });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, fileId]);

  // --- Resaltado bidireccional + visibilidad (aislar/ocultar): siguen a la selección ---
  // «¿Qué me falta?» se repinta solo cuando cambia lo que se construye o la hoja (repintar es caro).
  useEffect(() => {
    aplicarColorFalta();
    aplicarResaltado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queMeFalta, renglones]);

  useEffect(() => {
    aplicarResaltado();
    aplicarVisibilidad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsSel, capasSel, renglones, visMode, vistaSel, vistas, filtroConcepto, comparacionPorEntidad, capasOcultas, tramasOcultas]);

  /** Recorre el modelspace y arma los índices capa/bloque por HANDLE (una vez por apertura). */
  function armarCenso(
    instancia: AcApDocManager,
    AcDbBlockReference: typeof import('@mlightcad/data-model').AcDbBlockReference,
  ): void {
    const entidadesCenso: EntidadDelCenso[] = [];
    const byBlock = new Map<string, string[]>();
    const handleLayer = new Map<string, string>();
    const handleBlock = new Map<string, string>();
    const handleCentro = new Map<string, { x: number; y: number }>();
    const handleTamano = new Map<string, number>();
    const handleEnt = new Map<string, AcDbEntity>();
    const tramas = new Set<string>();
    try {
      const db = instancia.curDocument.database;
      const ms = db.tables.blockTable.modelSpace;
      for (const ent of ms.newIterator()) {
        const e = ent as AcDbEntity;
        const handle = safe(() => e.objectId);
        if (!handle) continue;
        const capa = safe(() => e.layer) ?? '0';
        handleLayer.set(handle, capa);
        handleEnt.set(handle, e);
        if (/hatch/i.test(safe(() => e.dxfTypeName) ?? '')) tramas.add(handle);
        const ext = safe(() => (e as unknown as { geometricExtents?: { min: { x: number; y: number }; max: { x: number; y: number } } }).geometricExtents);
        if (ext && Number.isFinite(ext.min.x) && Number.isFinite(ext.max.x)) {
          handleCentro.set(handle, { x: (ext.min.x + ext.max.x) / 2, y: (ext.min.y + ext.max.y) / 2 });
          // Textos, cotas y achurados no son lo que se construye: no pesan (su caja es enorme y se llevaría el porcentaje).
          const tipo = safe(() => e.dxfTypeName) ?? '';
          if (!/text|dimension|hatch|leader|attdef|attrib/i.test(tipo)) handleTamano.set(handle, Math.hypot(ext.max.x - ext.min.x, ext.max.y - ext.min.y));
        }
        const esBloque = e instanceof AcDbBlockReference;
        entidadesCenso.push({ handle, capa, esBloque });
        if (esBloque) {
          const nombre = safe(() => e.blockName);
          if (nombre) {
            handleBlock.set(handle, nombre);
            push(byBlock, nombre, handle);
          }
        }
      }
    } catch {
      /* si el censo falla, el clic cae al pick puntual sin "todas las iguales" */
    }
    // La capa = su geometría suelta, como la mide el lector; los INSERT van solo por `byBlock`.
    byLayerRef.current = armarCensoDeCapas(entidadesCenso).porCapaSuelta;
    byBlockRef.current = byBlock;
    handleLayerRef.current = handleLayer;
    handleBlockRef.current = handleBlock;
    handleCentroRef.current = handleCentro;
    handleTamanoRef.current = handleTamano;
    handleEntRef.current = handleEnt;
    handleTramaRef.current = tramas;
  }

  /** Handles con concepto PRINCIPAL en la hoja (lo que acompaña no da por presupuestado). */
  function handlesAsignados(): Set<string> {
    const s = new Set<string>();
    for (const r of renglones) {
      const sel = r.seleccion;
      if (!sel || r.rol !== 'principal') continue;
      if (sel.ids && sel.ids.length > 0) for (const id of sel.ids) s.add(id);
      else if (sel.tipo === 'capa') for (const id of handlesDeCapa(sel.clave, sel.vista ?? null)) s.add(id);
      else for (const id of byBlockRef.current.get(sel.clave) ?? []) s.add(id);
    }
    return s;
  }

  /**
   * Pinta el plano para «¿Qué me falta?» o le devuelve sus colores. El motor no deja teñir por objeto
   * (ver `aplicarResaltado`), así que se cambia el COLOR de cada entidad y se le pide que la vuelva a
   * dibujar. Un bloque toma el color de su inserción solo si su contenido es «por bloque»: mientras
   * dure la vista, el contenido de los bloques se pasa a «por bloque» (y vuelve a su color al salir).
   */
  function aplicarColorFalta(): void {
    const inst = dmRef.current;
    const dmod = dataModelRef.current;
    if (!inst || !dmod) return;
    const originales = coloresOriginalesRef.current;
    if (!queMeFalta && originales.size === 0) return;
    const enModelo = new Set(handleEntRef.current.values());
    const redibujar = new Set<AcDbEntity>();
    const poner = (e: AcDbEntity, color: AcCmColor) => {
      try {
        if (!originales.has(e)) originales.set(e, e.color.clone());
        e.color = color;
        if (enModelo.has(e)) redibujar.add(e);
      } catch {
        /* best effort */
      }
    };
    // Primero todo vuelve a su color; si la vista sigue prendida, se vuelve a pintar encima.
    for (const [e, c] of originales) {
      try {
        e.color = c;
        if (enModelo.has(e)) redibujar.add(e);
      } catch {
        /* best effort */
      }
    }
    originales.clear();
    if (queMeFalta) {
      const aPresupuestar = new Set<string>();
      for (const capa of queMeFalta.capas) for (const h of byLayerRef.current.get(capa) ?? []) aPresupuestar.add(h);
      for (const b of queMeFalta.bloques) for (const h of byBlockRef.current.get(b) ?? []) aPresupuestar.add(h);
      const asignados = handlesAsignados();
      const rgb = (n: number) => new dmod.AcCmColor().setRGB((n >> 16) & 255, (n >> 8) & 255, n & 255);
      let fondoOscuro = true;
      try {
        const bg = inst.curView.backgroundColor;
        fondoOscuro = ((bg >> 16) & 255) * 0.299 + ((bg >> 8) & 255) * 0.587 + (bg & 255) * 0.114 < 128;
      } catch {
        /* sin fondo conocido: oscuro, el default del visor */
      }
      const falta = rgb(COLOR_FALTA);
      const hecho = rgb(fondoOscuro ? 0x8b9198 : 0x9aa0a6);
      const resto = rgb(fondoOscuro ? 0x3a3f45 : 0xdde0e3);
      const porBloque = new dmod.AcCmColor();
      porBloque.colorIndex = 0;
      const vistos = new Set<unknown>();
      const contenidoPorBloque = (ref: AcDbEntity) => {
        const btr = (ref as unknown as { blockTableRecord?: { newIterator(): Iterable<AcDbEntity> } }).blockTableRecord;
        if (!btr || vistos.has(btr)) return;
        vistos.add(btr);
        for (const hijo of btr.newIterator()) {
          poner(hijo, porBloque);
          if (hijo instanceof dmod.AcDbBlockReference) contenidoPorBloque(hijo);
        }
      };
      for (const [h, e] of handleEntRef.current) {
        poner(e, !aPresupuestar.has(h) ? resto : asignados.has(h) ? hecho : falta);
        if (e instanceof dmod.AcDbBlockReference) contenidoPorBloque(e);
      }
    }
    if (redibujar.size > 0) {
      try {
        // El motor guarda el dibujo de cada bloque por nombre + color: sin vaciarlo, un bloque se
        // redibuja con el contenido de antes.
        dmod.AcDbRenderingCache.instance.clear();
        inst.curView.updateEntity([...redibujar]);
      } catch {
        /* best effort */
      }
    }
  }

  /** ¿La entidad cae dentro de la caja de esa vista? (por su centro). Sin centro conocido: sí. */
  function enVista(handle: string, vistaId: string): boolean {
    const v = vistas?.find((x) => x.id === vistaId);
    const c = handleCentroRef.current.get(handle);
    if (!v || !c) return true;
    const [x0, y0, x1, y1] = v.caja;
    return c.x >= Math.min(x0, x1) && c.x <= Math.max(x0, x1) && c.y >= Math.min(y0, y1) && c.y <= Math.max(y0, y1);
  }

  /** Handles de una capa, solo los de la vista si se pide una. */
  function handlesDeCapa(capa: string, vistaId?: string | null): string[] {
    const todos = byLayerRef.current.get(capa) ?? [];
    return vistaId ? todos.filter((h) => enVista(h, vistaId)) : todos;
  }

  /** Clic izquierdo SIN arrastre = seleccionar. El pan (botón medio) y la caja de selección
   *  (arrastre con izq) los maneja el visor de forma nativa (como AutoCAD); acá solo detectamos el
   *  clic-sin-arrastre para no disparar selección simple encima de un gesto de arrastre. */
  function cablearClics(instancia: AcApDocManager): void {
    const cont = containerRef.current;
    if (!cont) return;
    const canvas = cont.querySelector('canvas');
    if (!canvas) return;

    let down: { x: number; y: number } | null = null;
    const onDown = (ev: PointerEvent) => {
      if (ev.button !== 0) return; // solo botón izquierdo
      // Alt+clic es un atajo del visor: evita que Windows lleve el foco a la barra de menús.
      if (ev.altKey) ev.preventDefault();
      down = { x: ev.clientX, y: ev.clientY };
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.button !== 0 || !down) return;
      const movido = Math.abs(ev.clientX - down.x) + Math.abs(ev.clientY - down.y);
      const inicio = down;
      down = null;
      if (movido > CLICK_VS_DRAG_PX) return; // fue arrastre (caja de selección nativa), no clic
      // «Solo una pieza»: Ctrl+clic (⌘ en Mac), que es lo que anuncia el panel «¿Qué quieres presupuestar?»; Alt+clic también vale.
      resolverClic(instancia, canvas as HTMLCanvasElement, inicio.x, inicio.y, ev.altKey || ev.ctrlKey || ev.metaKey);
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
  }

  /** Traduce un clic a una PlanoSeleccion (bloque → todas las iguales; si no, capa) y la emite.
   *
   *  HIT = SOLO `view.pick(pt, radio, pickOneOnly=false)`. El pick es una consulta espacial
   *  GEOMÉTRICA (devuelve las entidades cuya geometría cae dentro del recuadro de `HIT_RADIUS_PX`),
   *  SIN estado: el mismo clic da SIEMPRE el mismo resultado. Cada hit trae `.id` = handle de NIVEL
   *  SUPERIOR (el INSERT para un bloque, la entidad suelta si no), ya alineado con el censo
   *  (`handleBlockRef` / `handleLayerRef`), así que un clic sobre la geometría de una puerta/símbolo
   *  resuelve al INSERT y uno sobre un muro resuelve a su capa.
   *
   *  POR QUÉ NO `view.select`: antes se combinaba `view.select(pt)` con el pick. `select` acierta por
   *  el BBOX de la entidad (no por su geometría), así que agarra un bloque aunque cliquees su interior
   *  vacío, y —peor— CICLA: clics idénticos sobre el mismo punto devuelven entidades distintas una tras
   *  otra (como el "cicla la selección" de AutoCAD). Ese ciclado era el bug "un clic selecciona una cosa,
   *  el siguiente otra" (visto 2026-09-29: 1er clic la capa del muro, 2º clic el mueble encima). El pick
   *  geométrico no cicla y no agarra por bbox → determinista y "cliqueas lo que ves". */
  function resolverClic(
    instancia: AcApDocManager,
    canvas: HTMLCanvasElement,
    clientX: number,
    clientY: number,
    alt = false,
  ): void {
    try {
      const view = instancia.curView;
      const rect = canvas.getBoundingClientRect();
      // `pick` (como `select`) espera el punto en COORDENADAS DEL DIBUJO (WCS), no en píxeles del
      // canvas: hay que convertir con `screenToWorld`. El radio de acierto sí va en píxeles (el motor
      // lo convierte a WCS internamente). OJO: el código viejo le pasaba a `pick` píxeles del canvas,
      // así que `pick` SIEMPRE devolvía vacío y TODO el clic caía en `select` (el que cicla) — por eso
      // se veía el ciclado. Con la conversión correcta, `pick` hace el trabajo y `select` sobra.
      const puntoWcs = view.screenToWorld({ x: clientX - rect.left, y: clientY - rect.top });

      const handles: string[] = [];
      // TODOS los hits geométricos del punto (pickOneOnly=false): el `.id` de cada uno es el handle
      // de nivel superior (INSERT si es bloque). Determinista y sin ciclado (a diferencia de select).
      try {
        const hits = view.pick(puntoWcs, HIT_RADIUS_PX, false) ?? [];
        for (const h of hits) if (h.id) handles.push(h.id);
      } catch {
        /* best effort: ante un error del pick, el clic queda sin efecto */
      }

      // Lo oculto (ojo de capa, tramas, ocultar/aislar) no se elige con un clic.
      const ocultos = ocultosRef.current;
      if (ocultos.size > 0) {
        for (let i = handles.length - 1; i >= 0; i--) if (ocultos.has(handles[i]!)) handles.splice(i, 1);
      }
      if (handles.length === 0) return; // clic en vacío: no cambia la selección

      // Preferir un BLOQUE si hay alguno entre los candidatos (un clic sobre un INSERT que comparte
      // punto con geometría suelta debe seleccionar el bloque, no la capa de fondo).
      const handleBloque = handles.find((h) => handleBlockRef.current.has(h));
      if (handleBloque) {
        const blockName = handleBlockRef.current.get(handleBloque)!;
        // Ctrl/⌘/Alt+clic: SOLO esa inserción; clic normal: todas las del mismo nombre.
        const ids = alt ? [handleBloque] : byBlockRef.current.get(blockName) ?? [handleBloque];
        onSeleccion({ tipo: 'bloque', clave: blockName, ids });
        return;
      }

      // Si no hay bloque, resolver por CAPA con el primer handle.
      const handle = handles[0]!;
      const capa =
        handleLayerRef.current.get(handle) ?? resolverCapaEntidad(instancia, handle);
      if (capa) {
        const ids = byLayerRef.current.get(capa) ?? [handle];
        // Varias plantas en el archivo: el clic dice en cuál cayó (solo plantas que suman).
        const plantas = (vistas ?? []).filter((v) => v.suma);
        const vista = plantas.length >= 2 ? plantas.find((v) => enVista(handle, v.id))?.id : undefined;
        onSeleccion(vista ? { tipo: 'capa', clave: capa, ids, vista } : { tipo: 'capa', clave: capa, ids });
      }
    } catch {
      /* clic sin efecto ante un error puntual del pick */
    }
  }

  /** Fallback: resolver la capa de una entidad por su handle si no estaba en el censo. */
  function resolverCapaEntidad(instancia: AcApDocManager, handle: string): string | null {
    try {
      const ent = instancia.curDocument.entityService.getEntitiesByIds([handle])[0];
      return ent ? (safe(() => ent.layer) ?? null) : null;
    } catch {
      return null;
    }
  }

  /** Expande la selección actual (bloques por handle + capas por nombre) a un set de HANDLES. */
  function handlesDeLaSeleccion(): Set<string> {
    const sel = new Set<string>();
    for (const id of idsSel ?? []) sel.add(id);
    for (const capa of capasSel ?? []) {
      for (const id of handlesDeCapa(capa, vistaSel?.capa === capa ? vistaSel.vista : null)) sel.add(id);
    }
    return sel;
  }

  /** Resalta la selección actual + lo ya asignado.
   *
   *  OJO (punto 3): el resaltado del motor es un TINTE PLANO de color fijo (cian `0x08e8de`, no
   *  configurable por API pública). En un plano donde una capa nativa ya es de ese tono (p.ej. las
   *  cotas en cs_05), lo resaltado se confunde con lo no resaltado. No hay API pública para cambiar
   *  ese color, ni para engrosar/perfilar el trazo, ni para atenuar por objeto (la geometría va
   *  BATCHED → materiales compartidos, sin opacidad por objeto). La vía color-independiente que SÍ
   *  ofrece el motor es ocultar/aislar (ver `aplicarVisibilidad`, controles "Aislar/Ocultar"). */
  function aplicarResaltado(): void {
    const inst = dmRef.current;
    if (!inst) return;
    try {
      const view = inst.curView;
      const objetivo = new Set<string>();
      // Selección actual (bloques por handle + capas por nombre).
      for (const id of handlesDeLaSeleccion()) objetivo.add(id);
      // Lo ya asignado en la HP (resaltado bidireccional plano↔HP). Con «¿Qué me falta?» lo dice el color.
      for (const r of queMeFalta ? [] : renglones) {
        const sel = r.seleccion;
        if (!sel) continue;
        if (sel.ids && sel.ids.length > 0) {
          for (const id of sel.ids) objetivo.add(id);
        } else if (sel.tipo === 'capa') {
          for (const id of handlesDeCapa(sel.clave, sel.vista ?? null)) objetivo.add(id);
        }
      }
      // «Cambió el plano»: con un concepto elegido, lo suyo que NO cambió se ve resaltado (lo que cambió ya lleva su color).
      if (comparacionPorEntidad && filtroConcepto) {
        for (const capa of filtroConcepto.capas) for (const h of byLayerRef.current.get(capa) ?? []) if (!comparacionPorEntidad.has(h)) objetivo.add(h);
        for (const b of filtroConcepto.bloques) for (const h of handlesDeBloque(b)) if (!comparacionPorEntidad.has(h)) objetivo.add(h);
      }
      const nuevos = [...objetivo];
      const previos = resaltadosRef.current;
      if (previos.length > 0) {
        try { view.unhighlight(previos); } catch { /* best effort */ }
      }
      if (nuevos.length > 0) {
        try { view.highlight(nuevos); } catch { /* best effort */ }
      }
      resaltadosRef.current = nuevos;
    } catch {
      /* best effort */
    }
  }

  /** Aísla u oculta la selección actual (punto 4). Solo VISTA: usa `view.setEntitySceneVisible`
   *  (transitorio, no toca la base del dibujo). Recorre TODOS los handles top-level del censo. */
  function aplicarVisibilidad(): void {
    const inst = dmRef.current;
    if (!inst) return;
    try {
      const view = inst.curView;
      // Un solo conjunto de ocultos: ojos de capa + tramas + aislar/ocultar la selección (ver `lib/visibilidadPlano`).
      const { visibles, ocultos } = handlesVisibles(
        (function* () {
          for (const [handle, capa] of handleLayerRef.current) yield { handle, capa, esTrama: handleTramaRef.current.has(handle) };
        })(),
        { capasOcultas: capasOcultas ?? new Set<string>(), tramasOcultas, seleccion: handlesDeLaSeleccion(), modo: visMode },
      );
      ocultosRef.current = ocultos;
      // Las capas apagadas con su ojo se apagan con la función NATIVA del motor (AutoCAD: capa apagada): ocultar las
      // entidades de una en una (`setEntitySceneVisible`) no quita las líneas sueltas, que el motor pinta en lote.
      // Solo se vuelven a prender las que apagó este control: una capa que el dibujo ya traía apagada no se toca.
      try {
        const almacen = (inst.curDocument as unknown as { layerStore?: { setLayerOn(nombre: string, encendida: boolean): boolean } }).layerStore;
        const quiero = capasOcultas ?? new Set<string>();
        for (const c of quiero) {
          if (!capasApagadasRef.current.has(c) && almacen?.setLayerOn(c, false)) capasApagadasRef.current.add(c);
        }
        for (const c of [...capasApagadasRef.current]) {
          if (!quiero.has(c)) {
            almacen?.setLayerOn(c, true);
            capasApagadasRef.current.delete(c);
          }
        }
      } catch {
        /* best effort */
      }
      for (const h of visibles) {
        try { view.setEntitySceneVisible(h, true); } catch { /* best effort */ }
      }
      for (const h of ocultos) {
        try { view.setEntitySceneVisible(h, false); } catch { /* best effort */ }
      }
    } catch {
      /* best effort */
    }
  }

  /** Dónde cae en pantalla lo seleccionado (o todo el dibujo: `todo` o sin selección), por los centros de sus entidades. */
  function cajaEnPantalla(todo?: boolean): CajaEnPantalla | null {
    const inst = dmRef.current;
    if (!inst) return null;
    try {
      const sel = todo ? new Set<string>() : handlesDeLaSeleccion();
      const handles = sel.size > 0 ? sel : new Set(handleCentroRef.current.keys());
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const h of handles) {
        const c = handleCentroRef.current.get(h);
        if (!c) continue;
        if (c.x < x0) x0 = c.x;
        if (c.y < y0) y0 = c.y;
        if (c.x > x1) x1 = c.x;
        if (c.y > y1) y1 = c.y;
      }
      if (!Number.isFinite(x0)) return null;
      const view = inst.curView;
      const a = view.worldToScreen({ x: x0, y: y0 });
      const b = view.worldToScreen({ x: x1, y: y1 });
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const w = Math.abs(a.x - b.x);
      const h = Math.abs(a.y - b.y);
      return Number.isFinite(x + y + w + h) ? { x, y, w, h } : null;
    } catch {
      return null;
    }
  }
  // Se re-entrega en cada render para que lea siempre la selección vigente.
  useEffect(() => {
    if (!onCajaEnPantalla) return;
    onCajaEnPantalla(!estado.cargando && !estado.error ? cajaEnPantalla : null);
  });
  useEffect(() => () => onCajaEnPantalla?.(null), [onCajaEnPantalla]);

  /**
   * El croquis de unos elementos: se pintan con el modo de comparación del visor (solo vista: el resto en
   * gris, lo pedido en el acento, fondo blanco), se encuadran y se lee el lienzo JUSTO después de que el
   * visor dibuja (sin preserveDrawingBuffer el lienzo WebGL solo se puede leer en ese mismo cuadro). Después
   * todo vuelve a como estaba: colores, resaltado y encuadre.
   */
  /** Los handles de unos elementos (capa en su vista, o bloque con sus piezas). */
  function handlesDeElementos(elementos: ElementoParaCroquis[]): Set<string> {
    const handles = new Set<string>();
    for (const e of elementos) {
      if (e.tipo === 'bloque') for (const h of e.ids?.length ? e.ids : byBlockRef.current.get(e.clave) ?? []) handles.add(h);
      else for (const h of handlesDeCapa(e.clave, e.vista ?? null)) handles.add(h);
    }
    return handles;
  }

  async function capturarCroquis(
    elementos: ElementoParaCroquis[],
    marcasPedidas: { k: string; elementos: ElementoParaCroquis[] }[] = [],
  ): Promise<Croquis | null> {
    const inst = dmRef.current;
    const dmod = dataModelRef.current;
    const canvas = containerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!inst || !dmod || !canvas) return null;
    const handles = handlesDeElementos(elementos);
    if (handles.size === 0) return null;
    const view = inst.curView;
    const caja = new dmod.AcGeBox2d();
    for (const h of handles) {
      const ext = safe(() => (handleEntRef.current.get(h) as unknown as { geometricExtents?: { min: { x: number; y: number }; max: { x: number; y: number } } } | undefined)?.geometricExtents);
      if (ext && Number.isFinite(ext.min.x) && Number.isFinite(ext.max.x)) {
        caja.expandByPoint({ x: ext.min.x, y: ext.min.y });
        caja.expandByPoint({ x: ext.max.x, y: ext.max.y });
      } else {
        const c = handleCentroRef.current.get(h);
        if (c) caja.expandByPoint(c);
      }
    }
    if (caja.isEmpty()) return null;
    // Lo que se veía antes, para volver.
    const r = canvas.getBoundingClientRect();
    const antes = new dmod.AcGeBox2d();
    antes.expandByPoint(view.screenToWorld({ x: 0, y: 0 }));
    antes.expandByPoint(view.screenToWorld({ x: r.width, y: r.height }));
    const fondoAntes = view.backgroundColor;
    let url: string | null = null;
    const marcas: Croquis['marcas'] = [];
    try {
      try { view.unhighlight(resaltadosRef.current); } catch { /* best effort */ }
      // Lo que la persona ocultó o aisló se vuelve a ver para el croquis (el resto va atenuado).
      for (const h of handleLayerRef.current.keys()) {
        try { view.setEntitySceneVisible(h, true); } catch { /* best effort */ }
      }
      view.setCompareDisplay({
        enabled: true,
        baseColor: CROQUIS_RESTO,
        colors: { added: CROQUIS_RESALTE },
        overrides: [...handles].map((objectId) => ({ objectId, role: 'added' as const })),
      });
      view.backgroundColor = CROQUIS_FONDO;
      view.zoomTo(caja, 1.25);
      // Que el visor termine de repintar con los colores del croquis y el encuadre nuevo (unos cuadros).
      await view.waitUntilIdle(4000).catch(() => false);
      for (let i = 0; i < 3; i++) await new Promise<void>((ok) => requestAnimationFrame(() => ok()));
      // Sincronizar con un cuadro, volver a encuadrar (marca el dibujo como sucio) y leer en el cuadro
      // siguiente, justo después de que el visor dibuja.
      view.zoomTo(caja, 1.25);
      url = await new Promise<string | null>((ok) =>
        requestAnimationFrame(() => {
          try {
            const u = reducirLienzo(canvas, CROQUIS_ANCHO_MAX);
            // Dónde cae cada elemento rotulado: el centro de sus entidades, en la vista del croquis.
            const rc = canvas.getBoundingClientRect();
            for (const mk of marcasPedidas) {
              let sx = 0, sy = 0, n = 0;
              for (const h of handlesDeElementos(mk.elementos)) {
                const c = handleCentroRef.current.get(h);
                if (c) { sx += c.x; sy += c.y; n++; }
              }
              if (!n || !(rc.width > 0) || !(rc.height > 0)) continue;
              const p = view.worldToScreen({ x: sx / n, y: sy / n });
              const x = p.x / rc.width;
              const y = p.y / rc.height;
              if (x >= 0 && x <= 1 && y >= 0 && y <= 1) marcas.push({ k: mk.k, x, y });
            }
            ok(u);
          } catch {
            ok(null);
          }
        }),
      );
    } catch {
      url = null;
    } finally {
      try { view.setCompareDisplay({ enabled: false, overrides: [] }); } catch { /* best effort */ }
      try { view.backgroundColor = fondoAntes; } catch { /* best effort */ }
      try { view.zoomTo(antes, 1); } catch { /* best effort */ }
      resaltadosRef.current = [];
      aplicarResaltado();
      aplicarVisibilidad();
    }
    return url ? { url, marcas } : null;
  }
  /** Los handles de un bloque por su nombre; el del inventario puede venir sin el prefijo de xref («…$0$puerta 0.90»). */
  function handlesDeBloque(nombre: string): string[] {
    const exacto = byBlockRef.current.get(nombre);
    if (exacto) return exacto;
    const out: string[] = [];
    for (const [k, hs] of byBlockRef.current) if (k.split('$').pop() === nombre) out.push(...hs);
    return out;
  }

  // «Cambió el plano»: modo de comparación del motor sobre lo que cambió (los mismos tonos que el modelo IFC).
  const compararActivoRef = useRef(false);
  useEffect(() => {
    const inst = dmRef.current;
    if (estado.cargando || estado.error || !inst) return;
    const view = inst.curView;
    try {
      // Entidad por entidad (con el filtro de un concepto, si lo hay).
      let porEntidad: { objectId: string; role: 'added' | 'deleted' | 'modified' }[] = [];
      if (comparacionPorEntidad && comparacionPorEntidad.size > 0) {
        let permitidos: Set<string> | null = null;
        if (filtroConcepto) {
          permitidos = new Set<string>();
          for (const capa of filtroConcepto.capas) for (const h of byLayerRef.current.get(capa) ?? []) permitidos.add(h);
          for (const b of filtroConcepto.bloques) for (const h of handlesDeBloque(b)) permitidos.add(h);
        }
        for (const [h, rol] of comparacionPorEntidad) if (!permitidos || permitidos.has(h)) porEntidad.push({ objectId: h, role: rol });
      }
      if (porEntidad.length === 0 && !resaltarCambio && !(comparacionPorEntidad && filtroConcepto)) {
        if (compararActivoRef.current) {
          view.setCompareDisplay({ enabled: false, overrides: [] });
          compararActivoRef.current = false;
        }
        return;
      }
      const handles = new Set<string>();
      if (porEntidad.length === 0 && resaltarCambio) {
        for (const capa of resaltarCambio.capas) for (const h of byLayerRef.current.get(capa) ?? []) handles.add(h);
        for (const b of resaltarCambio.bloques) for (const h of handlesDeBloque(b)) handles.add(h);
      }
      let fondoOscuro = true;
      try {
        const bg = view.backgroundColor;
        fondoOscuro = ((bg >> 16) & 255) * 0.299 + ((bg >> 8) & 255) * 0.587 + (bg & 255) * 0.114 < 128;
      } catch {
        /* sin fondo conocido: oscuro, el default del visor */
      }
      view.setCompareDisplay({
        enabled: true,
        baseColor: coloresComparacion?.base ?? (fondoOscuro ? 0x3a3f45 : 0xdde0e3),
        colors: {
          added: coloresComparacion?.added ?? COLOR_CAMBIO_NUEVO,
          deleted: coloresComparacion?.deleted ?? COLOR_FALTA,
          modified: coloresComparacion?.modified ?? COLOR_CAMBIO_MEDIDA,
        },
        overrides: porEntidad.length > 0 ? porEntidad : [...handles].map((objectId) => ({ objectId, role: resaltarCambio!.rol })),
      });
      compararActivoRef.current = true;
    } catch {
      /* best effort */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resaltarCambio, comparacionPorEntidad, filtroConcepto, coloresComparacion, estado.cargando, estado.error]);

  // Enfoque pedido desde afuera (p. ej. «Cambió el plano»): encuadra las entidades de esas capas y bloques.
  const habiaEnfoqueRef = useRef(false);
  useEffect(() => {
    if (estado.cargando || estado.error) return;
    if (!enfocar) {
      // Al quitar el enfoque se vuelve a encuadrar el dibujo entero: el acercamiento pudo quedar lejos del plano.
      if (habiaEnfoqueRef.current) {
        habiaEnfoqueRef.current = false;
        try {
          dmRef.current?.curView.zoomToFitDrawing();
        } catch {
          /* mejor esfuerzo */
        }
      }
      return;
    }
    habiaEnfoqueRef.current = true;
    const inst = dmRef.current;
    const dmod = dataModelRef.current;
    if (!inst || !dmod) return;
    const handles = new Set<string>();
    for (const capa of enfocar.capas) for (const h of byLayerRef.current.get(capa) ?? []) handles.add(h);
    for (const b of enfocar.bloques) for (const h of handlesDeBloque(b)) handles.add(h);
    for (const h of enfocar.handles ?? []) handles.add(h);
    // Con el diff por entidad, el enfoque de una capa o bloque va a lo que CAMBIÓ dentro de ella (si algo cambió).
    if (comparacionPorEntidad && comparacionPorEntidad.size > 0 && enfocar.capas.length + enfocar.bloques.length > 0) {
      const cambiados = [...handles].filter((h) => comparacionPorEntidad.has(h));
      if (cambiados.length > 0) {
        handles.clear();
        for (const h of cambiados) handles.add(h);
      }
    }
    if (handles.size === 0) return;
    // Solo con los CENTROS ya censados (barato y siempre finitos): pedir la extensión de cada entidad congelaba el
    // navegador con un dibujo real (alguna entidad trae una extensión inválida y el motor no sale del zoom).
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const h of handles) {
      // El centro censado; si la entidad no lo trae (un bloque sin extensión finita), su punto de inserción.
      let c = handleCentroRef.current.get(h);
      if (!c) {
        const e = handleEntRef.current.get(h) as unknown as { position?: { x: number; y: number } } | undefined;
        c = e?.position;
      }
      if (!c || !Number.isFinite(c.x) || !Number.isFinite(c.y)) c = centrosExtra?.get(h);
      if (!c || !Number.isFinite(c.x) || !Number.isFinite(c.y)) continue;
      if (c.x < x0) x0 = c.x;
      if (c.y < y0) y0 = c.y;
      if (c.x > x1) x1 = c.x;
      if (c.y > y1) y1 = c.y;
    }
    if (!Number.isFinite(x0) || !Number.isFinite(y0) || !Number.isFinite(x1) || !Number.isFinite(y1)) return;
    // Un margen mínimo (un solo punto no es una caja) y proporcional a lo que abarca.
    const margen = Math.max((x1 - x0) * 0.05, (y1 - y0) * 0.05, 1.5);
    const caja = new dmod.AcGeBox2d();
    caja.expandByPoint({ x: x0 - margen, y: y0 - margen });
    caja.expandByPoint({ x: x1 + margen, y: y1 + margen });
    if (caja.isEmpty()) return;
    // Un instante después: el visor termina de encuadrar todo el dibujo al abrirlo y eso pisaría este zoom.
    // Se repite unas veces: si el diálogo cambia de tamaño al abrirse, el motor vuelve a encuadrar todo y pisa el zoom.
    const ts = [400, 1500, 3500].map((ms) =>
      setTimeout(() => {
        try {
          inst.curView.zoomTo(caja, 1.25);
        } catch {
          /* best effort */
        }
      }, ms),
    );
    return () => ts.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enfocar?.n, estado.cargando, estado.error]);

  const capturarRef = useRef(capturarCroquis);
  capturarRef.current = capturarCroquis;
  useEffect(() => {
    if (!onCapturador) return;
    onCapturador(!estado.cargando && !estado.error ? (els, mk) => capturarRef.current(els, mk) : null);
  }, [onCapturador, estado.cargando, estado.error]);
  useEffect(() => () => onCapturador?.(null), [onCapturador]);

  const btnDisabled = !haySeleccion;
  const hayOculto = visMode !== 'todo' || hayOjosApagados(capasOcultas ?? new Set<string>(), tramasOcultas);
  function restablecerVisibilidad(): void {
    setVisMode('todo');
    onRestablecerOjos?.();
  }

  // Esc = mostrar todo (si no se está escribiendo en un campo ni hay un diálogo abierto).
  const restablecerRef = useRef(restablecerVisibilidad);
  restablecerRef.current = restablecerVisibilidad;
  const hayOcultoRef = useRef(hayOculto);
  hayOcultoRef.current = hayOculto;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !hayOcultoRef.current) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.closest('[role="combobox"],[role="listbox"],[role="dialog"]'))) return;
      restablecerRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

      {/* Controles de visibilidad (aislar/ocultar), como ISOLATE/HIDE de AutoCAD. Coherentes con
          la toolbar del visor SVG en PlanosPage (mismos tokens de color/borde/sombra). */}
      {!estado.cargando && !estado.error && !sinControles && (
        <div style={toolbarStyle}>
          <button
            type="button"
            style={botonStyle(visMode === 'aislar', btnDisabled)}
            disabled={btnDisabled}
            aria-pressed={visMode === 'aislar'}
            title="Deja visibles solo las piezas seleccionadas."
            onClick={() => setVisMode((v) => (v === 'aislar' ? 'todo' : 'aislar'))}
          >
            Aislar selección
          </button>
          <button
            type="button"
            style={botonStyle(visMode === 'ocultar', btnDisabled)}
            disabled={btnDisabled}
            aria-pressed={visMode === 'ocultar'}
            title="Oculta las piezas seleccionadas."
            onClick={() => setVisMode((v) => (v === 'ocultar' ? 'todo' : 'ocultar'))}
          >
            Ocultar selección
          </button>
          {onAlternarTramas && (
            <button
              type="button"
              style={botonStyle(tramasOcultas, false)}
              aria-pressed={tramasOcultas}
              title={tramasOcultas ? 'Vuelve a mostrar las tramas (achurados).' : 'Oculta todas las tramas (achurados), sin importar su capa.'}
              onClick={onAlternarTramas}
            >
              {tramasOcultas ? 'Mostrar tramas' : 'Ocultar tramas'}
            </button>
          )}
          <button
            type="button"
            style={botonStyle(false, !hayOculto)}
            disabled={!hayOculto}
            title="Vuelve a mostrar todo el plano (capas, tramas y piezas ocultas). También con Esc."
            onClick={restablecerVisibilidad}
          >
            Mostrar todo
          </button>
        </div>
      )}
      {!estado.cargando && !estado.error && !sinControles && hayOculto && (
        <div style={avisoOcultoStyle} role="note">Lo oculto sigue contando en la hoja</div>
      )}

      {/* Barra de ayuda fija (como la barra de estado de AutoCAD): anuncia los atajos de selección. */}
      {!estado.cargando && !estado.error && !sinControles && (
        <div style={barraAyudaStyle} role="note">
          {`Clic: toda la capa o todos los bloques iguales · ${teclaPieza()}+clic en un bloque: solo ese`}
        </div>
      )}

      {estado.cargando && !visorMuestraAvance && (
        <div style={overlayStyle}>
          <Spinner size="large" label="Abriendo el plano…" />
        </div>
      )}
      {estado.error && (
        <div style={overlayStyle}>
          <div style={{ maxWidth: 420, textAlign: 'center', color: 'var(--text-primary)' }}>
            {estado.error}
          </div>
        </div>
      )}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  background: 'var(--bg-page)',
  pointerEvents: 'none',
};

// Toolbar flotante arriba a la derecha (misma posición/estilo que la del visor SVG en PlanosPage).
const toolbarStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 5,
  display: 'flex',
  gap: 4,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: 4,
  boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.18))',
};

// Aviso bajo la barra de visibilidad: lo oculto es solo vista.
const avisoOcultoStyle: React.CSSProperties = {
  position: 'absolute',
  top: 56,
  right: 12,
  zIndex: 5,
  pointerEvents: 'none',
  fontSize: 11.5,
  color: 'var(--text-secondary)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: '2px 8px',
};

// Barra de ayuda abajo a la izquierda; no captura clics para no estorbar al dibujo.
const barraAyudaStyle: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  bottom: 12,
  zIndex: 5,
  maxWidth: 'calc(100% - 24px)',
  pointerEvents: 'none',
  fontSize: 12,
  color: 'var(--text-secondary)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  padding: '4px 10px',
  boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.18))',
};

/** Tecla del atajo «solo una pieza»: «⌘» en Mac, «Ctrl» en el resto (la misma que anuncia el panel de la derecha). */
function teclaPieza(): string {
  try {
    const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
    const plataforma = nav.userAgentData?.platform ?? nav.platform ?? '';
    return /mac|iphone|ipad/i.test(plataforma) ? '⌘' : 'Ctrl';
  } catch {
    return 'Ctrl';
  }
}

function botonStyle(activo: boolean, deshabilitado: boolean): React.CSSProperties {
  return {
    height: 30,
    padding: '0 10px',
    fontSize: 12,
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    cursor: deshabilitado ? 'default' : 'pointer',
    opacity: deshabilitado ? 0.5 : 1,
    background: activo ? 'var(--accent-weak)' : 'var(--bg-surface)',
    color: activo ? 'var(--accent)' : 'var(--text-primary)',
  };
}

// --- helpers puros ---
function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}
function push(m: Map<string, string[]>, k: string, v: string): void {
  const arr = m.get(k);
  if (arr) arr.push(v);
  else m.set(k, [v]);
}

export default PlanoViewerMlightcad;
